import { Box, Typography, IconButton, Tooltip, LinearProgress } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import React from 'react';
import AssigneeAvatarStack from '../../../shared/AssigneeAvatarStack';
import { 
  getPriorityColor, 
  getStatusColor, 
  getTaskTypeColor, 
  getAssigneeColor, 
  lightenColor 
} from '../calendar-view/calendarColorUtils';
import { getTaskBoundaryInfo } from './ganttUtils';
import { formatDateWithOptionalTime } from '../../../shared/timeUtils';
import { UI_LABELS } from '../../../shared/appConstants';
import './GanttTaskCard.scss';

const GanttTaskCard = ({ 
  tasks, 
  totalTasks,
  colorIndex, 
  style, 
  onClick,
  boundaryInfo,
  onBoundaryClick,
  groupByOption,
  activeTooltipTaskId,
  setActiveTooltipTaskId,
  dates,
  viewType,
  ...rest
}) => {
  const [hoveredAssigneeTaskId, setHoveredAssigneeTaskId] = React.useState(null);

  const getAssigneeNames = (task) => {
    const assignees = Array.isArray(task.assignees) && task.assignees.length > 0
      ? task.assignees
      : (task.assignee ? [task.assignee] : []);
    return assignees
      .map(a => a?.name || `${a?.first_name || ''} ${a?.last_name || ''}`.trim())
      .filter(Boolean);
  };

  const truncateName = (name, maxLength = 30) => {
    if (!name || name.length <= maxLength) return name;
    return name.substring(0, maxLength) + '...';
  };

  const formatAssigneeLabel = (names) => {
    if (names.length === 0) return 'Unassigned';
    const maxVisible = 10;
    const visibleNames = names.slice(0, maxVisible).map(name => truncateName(name));
    const remaining = names.length - maxVisible;
    return remaining > 0 
      ? `${visibleNames.join(', ')} +${remaining}`
      : visibleNames.join(', ');
  };

  const calculateTaskProgress = (task) => {
    const isCompleted = task.completed || task.marked_as_completed || task.is_completed || false;
    if (!task.subtasks?.length) return isCompleted ? 100 : 0;

    const calculateSubtaskProgress = (subtasks) => {
      let totalProgress = 0;
      let totalWeight = 0;

      subtasks.forEach((subtask) => {
        const subtaskCompleted = subtask.completed || subtask.marked_as_completed || subtask.is_completed || false;

        if (subtask.subtasks && subtask.subtasks.length > 0) {
          const nestedProgress = calculateSubtaskProgress(subtask.subtasks);
          totalProgress += nestedProgress;
        } else {
          totalProgress += subtaskCompleted ? 100 : 0;
        }
        totalWeight += 1;
      });

      return totalWeight > 0 ? totalProgress / totalWeight : 0;
    };

    return calculateSubtaskProgress(task.subtasks);
  };

  const formatTaskTooltip = (task) => {
    const startDate = task.startDate
      ? new Date(task.startDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' })
      : UI_LABELS.NOT_SET;
    const dueDate = task.dueDateRaw || task.dueDate
      ? formatDateWithOptionalTime(task.dueDateRaw || task.dueDate, task.hasDueTime, { locale: 'en-US', fallback: UI_LABELS.NOT_SET })
      : UI_LABELS.NOT_SET;
    const assigneeNames = getAssigneeNames(task);
    const assigneeLabel = formatAssigneeLabel(assigneeNames);
    const isEpic = String(task.taskType || '').toLowerCase().includes('epic');
    const hasSubtasks = Array.isArray(task.subtasks) && task.subtasks.length > 0;
    const showProgress = isEpic || hasSubtasks;
    const progress = showProgress ? calculateTaskProgress(task) : 0;

    return (
      <Box className="gantt-task-tooltip-content">
        <Typography className="gantt-task-tooltip-type">{task.taskType || 'Task'}</Typography>
        <Typography className="gantt-task-tooltip-title">
          <span className="gantt-task-tooltip-ticket">{task.ticketNumber}</span> {task.title}
        </Typography>
        {showProgress && (
          <LinearProgress
            variant="determinate"
            value={progress}
            className="gantt-task-tooltip-progress"
          />
        )}
        <Box className="gantt-task-tooltip-dates">
          <Box className="gantt-task-tooltip-date-col">
            <Typography className="gantt-task-tooltip-date-label">Start date</Typography>
            <Typography className="gantt-task-tooltip-date-value">{startDate}</Typography>
          </Box>
          <Box className="gantt-task-tooltip-date-col right">
            <Typography className="gantt-task-tooltip-date-label">Due Date</Typography>
            <Typography className="gantt-task-tooltip-date-value">{dueDate}</Typography>
          </Box>
        </Box>
        <Typography className="gantt-task-tooltip-assignee-label">Assignee</Typography>
        <Box className="gantt-task-tooltip-assignee-row">
          <AssigneeAvatarStack
            assignees={task.assignees}
            assignee={task.assignee}
            max={3}
            size={20}
            showTooltip={false}
            stackDirection="column"
          />
          <Typography className="gantt-task-tooltip-assignee-names">{assigneeLabel}</Typography>
        </Box>
      </Box>
    );
  };

  const getTaskColor = (task) => {
    if (!groupByOption) return null;

    switch (groupByOption) {
      case 'priority':
        return getPriorityColor(task.priority);
      case 'status':
        return getStatusColor(task.status);
      case 'taskType':
        return getTaskTypeColor(task.taskType);
      case 'assignee':
        return getAssigneeColor(task.assignees?.[0] || task.assignee);
      default:
        return null;
    }
  };

  const renderColorIndicator = (task) => {
    if (groupByOption === 'assignee') {
      const assignees = Array.isArray(task.assignees) && task.assignees.length > 0
        ? task.assignees
        : (task.assignee ? [task.assignee] : []);
      if (assignees.length === 0) return null;
      const visible = assignees.slice(0, 3);
      const extraCount = assignees.length - visible.length;
      return (
        <Box className="gantt-card-color-indicator-container">
          {visible.map((assignee, index) => {
            const color = getAssigneeColor(assignee);
            const lightColor = lightenColor(color, 85);
            return (
              <Box
                key={assignee?.id || assignee?.name || index}
                className="gantt-card-assignee-color-circle"
                style={{
                  '--gantt-circle-color': color,
                  '--gantt-circle-border': lightColor,
                  '--gantt-circle-z': visible.length - index
                }}
              />
            );
          })}
          {extraCount > 0 && (
            <Box
              key="assignee-more"
              className={`gantt-card-assignee-more${visible.length === 0 ? ' is-first' : ''}`}
            >
              +
            </Box>
          )}
        </Box>
      );
    }

    const color = getTaskColor(task);
    if (!color) return null;

    const lightColor = lightenColor(color, 85);
    
    return (
      <Box 
        className="gantt-card-single-color-indicator"
        style={{
          '--gantt-indicator-color': color,
          '--gantt-indicator-border': lightColor
        }}
      />
    );
  };

  const formatDateForTooltip = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDueDateForTooltip = (task) => (
    formatDateWithOptionalTime(task?.dueDateRaw || task?.dueDate, task?.hasDueTime, {
      locale: 'en-US',
      fallback: ''
    })
  );


  const taskBoundaryMap = React.useMemo(() => {
    const map = new Map();
    if (!Array.isArray(tasks) || tasks.length === 0) return map;
    if (!Array.isArray(dates) || dates.length === 0 || !viewType) return map;

    tasks.forEach((task, index) => {
      if (!task?.startDate || !task?.dueDate) return;
      const info = getTaskBoundaryInfo({ tasks: [task] }, dates, viewType);
      map.set(String(task.id ?? index), info);
    });

    return map;
  }, [tasks, dates, viewType]);

  if (!tasks || tasks.length === 0) {
    return null;
  }

  const incomingBoundaryTasks = boundaryInfo?.isIncoming
    ? tasks.filter((task, index) => {
        const info = taskBoundaryMap.get(String(task.id ?? index));
        return info?.isIncoming;
      })
    : [];

  const outgoingBoundaryTasks = boundaryInfo?.isOutgoing
    ? tasks.filter((task, index) => {
        const info = taskBoundaryMap.get(String(task.id ?? index));
        return info?.isOutgoing;
      })
    : [];
  
  const totalProgress = tasks.reduce((sum, task) => sum + calculateTaskProgress(task), 0);
  const averageProgress = tasks.length > 0 ? totalProgress / tasks.length : 0;
  const colorClass = averageProgress > 50 ? 'color-dark-blue' : 'color-blue';
  
  // Determine boundary classes
  const boundaryClasses = [];
  if (boundaryInfo?.isIncoming) boundaryClasses.push('incoming');
  if (boundaryInfo?.isOutgoing) boundaryClasses.push('outgoing');

  return (
    <Box
      className={`gantt-task-card ${colorClass} ${boundaryClasses.join(' ')}`}
      style={style}
      {...rest}
    >
    {/* Incoming arrows for boundary tasks */}
    {incomingBoundaryTasks.length > 0 && (
      <Box className="gantt-boundary-arrows incoming">
        {incomingBoundaryTasks.map((task, index) => (
          <Tooltip
            key={`incoming-${task.id ?? index}`}
            title={`Start Date: ${formatDateForTooltip(task?.startDate)}`}
            placement="top"
            arrow
            classes={{ tooltip: 'app-tooltip' }}
          >
            <IconButton
              className="gantt-boundary-arrow incoming-arrow"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onBoundaryClick?.(boundaryInfo, 'prev', task?.id);
              }}
            >
              <ChevronLeft fontSize="small" />
            </IconButton>
          </Tooltip>
        ))}
      </Box>
    )}
    
    <Box className="gantt-task-card-content">
      {tasks.map((task, index) => (
        <Tooltip
          key={task.id}
          title={formatTaskTooltip(task)}
          placement="bottom-start"
        classes={{ tooltip: 'gantt-task-tooltip gantt-task-tooltip-card' }}
          open={activeTooltipTaskId === task.id && hoveredAssigneeTaskId !== task.id}
          disableFocusListener
          disableTouchListener
          disableInteractive
          PopperProps={{
            modifiers: [
              {
                name: 'preventOverflow',
                options: {
                  boundary: 'viewport',
                  altBoundary: true,
                  padding: 8,
                },
              },
              {
                name: 'flip',
                options: {
                  fallbackPlacements: ['top-start', 'bottom-end', 'top-end'],
                },
              },
            ],
          }}
        >
          <Box 
            className="gantt-task-card-item"
            onClick={(e) => {
              e.stopPropagation();
              setActiveTooltipTaskId?.(null);
              setHoveredAssigneeTaskId(null);
              onClick?.(task);
            }}
            onMouseEnter={() => setActiveTooltipTaskId?.(task.id)}
            onMouseLeave={() => setActiveTooltipTaskId?.(null)}
          >
            <Box className="gantt-card-text-container">
              {renderColorIndicator(task)}
              <Typography className="gantt-task-card-text" variant="body2">
                {task.ticketNumber} {task.title}
              </Typography>
            </Box>
            {(task.assignees?.length || task.assignee) && (
              <Box
                className="gantt-task-card-avatar-stack"
                onMouseEnter={(e) => {
                  e.stopPropagation();
                  setHoveredAssigneeTaskId(task.id);
                }}
                onMouseLeave={(e) => {
                  e.stopPropagation();
                  setHoveredAssigneeTaskId(null);
                }}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <AssigneeAvatarStack
                  assignees={task.assignees}
                  assignee={task.assignee}
                  max={2}
                  size={16}
                  colorResolver={groupByOption === 'assignee' ? getAssigneeColor : null}
                  showTooltip={false}
                />
              </Box>
            )}
          </Box>
        </Tooltip>
      ))}
    </Box>
    
    {/* Outgoing arrows for boundary tasks */}
    {outgoingBoundaryTasks.length > 0 && (
      <Box className="gantt-boundary-arrows outgoing">
        {outgoingBoundaryTasks.map((task, index) => (
          <Tooltip
            key={`outgoing-${task.id ?? index}`}
            title={`Due Date: ${formatDueDateForTooltip(task)}`}
            placement="top"
            arrow
            classes={{ tooltip: 'app-tooltip' }}
          >
            <IconButton
              className="gantt-boundary-arrow outgoing-arrow"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onBoundaryClick?.(boundaryInfo, 'next', task?.id);
              }}
            >
              <ChevronRight fontSize="small" />
            </IconButton>
          </Tooltip>
        ))}
      </Box>
    )}
  </Box>
  );
};

export default GanttTaskCard;
